/**
 * How `searchText` is folded and composed.
 *
 * Ported from the app's `lib/core/utils/search_text.dart`, deliberately as a
 * literal translation rather than an improvement. `searchText` is written by
 * whichever client saves the record and read by every other one: a company
 * edited here and a company edited on a phone have to end up with the same
 * string, or the same query finds one and not the other. If the Dart file
 * changes, change this one with it.
 *
 * Turkish is why the folding is a table instead of `toLowerCase()`. JavaScript
 * maps `I` to `i` — right for English, wrong for Turkish — and leaves `İ` as an
 * `i` followed by a combining dot, so "İZMİR" would not match "izmir". Folding
 * every i-like letter to a plain `i` sidesteps both. CJK is left alone.
 */

const FOLDINGS: Record<string, string> = {
  ı: "i", İ: "i", I: "i", i: "i", î: "i", Î: "i", ï: "i",
  ş: "s", Ş: "s", ś: "s", š: "s",
  ğ: "g", Ğ: "g",
  ü: "u", Ü: "u", û: "u", Û: "u", ú: "u", ù: "u",
  ö: "o", Ö: "o", ô: "o", ó: "o", ò: "o", õ: "o", ø: "o",
  ç: "c", Ç: "c", ć: "c", č: "c",
  â: "a", Â: "a", á: "a", à: "a", ä: "a", ã: "a", å: "a",
  é: "e", è: "e", ê: "e", ë: "e",
  ñ: "n", ń: "n",
  ý: "y", ÿ: "y",
  ž: "z", ź: "z", ż: "z",
  ł: "l",
  ß: "ss",
};

/** Normalises a string for storage in, or comparison against, a search field. */
export function normalizeForSearch(input: string): string {
  let folded = "";
  // `for...of` walks code points, which is what the Dart original does with
  // `runes`; indexing by UTF-16 unit would split anything outside the BMP.
  for (const char of input) folded += FOLDINGS[char] ?? char.toLowerCase();
  // Collapse runs of whitespace so multi-line notes still match cleanly.
  return folded.replace(/\s+/g, " ").trim();
}

/** Joins every part worth matching on, dropping the blank ones. */
export function buildSearchText(parts: (string | null | undefined)[]): string {
  return normalizeForSearch(
    parts
      .filter((part): part is string => part != null && part.trim() !== "")
      .map((part) => part.trim())
      .join(" ")
  );
}
